import * as React from 'react';
import { useReactiveVar } from '@apollo/client';
import * as Apollo from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';

import {
  useGetSessionLazyQuery,
  useLoginMutation,
  useLogoutMutation,
  LoginMutationVariables,
  LoginMutation,
} from '@graphql';
import { loginStatusVar } from '@context';
import { useToastError } from '@hooks';
import { secureSetItem, secureRemoveItem, parseApolloError } from '@utils';

interface UseConnectionProps {
  redirect?: string | null;
}

export default function useConnection(props: Readonly<UseConnectionProps> = {}) {
  const { redirect = null } = props;
  const loginStatus = useReactiveVar(loginStatusVar);
  const toastError = useToastError();
  const navigate = useNavigate();
  const { i18n } = useTranslation('authentication');

  const [getSession] = useGetSessionLazyQuery();
  const [logUser] = useLoginMutation();

  const destinationAfterLogin = React.useMemo(() => {
    // priorité: redirection stockée (ex: page protégée) > redirect passé en props
    return loginStatus.redirection ?? redirect ?? null;
  }, [loginStatus.redirection, redirect]);

  const redirectAfterLogin = React.useCallback(() => {
    if (!destinationAfterLogin) return;
    navigate(destinationAfterLogin);
  }, [destinationAfterLogin, navigate]);

  const handleLogin = async (result: Awaited<ReturnType<typeof logUser>>) => {
    try {
      if (!result.data?.login) {
        return;
      }
      // store the access and refresh tokens in secure storage if on native platform (Capacitor)
      const { access, refresh } = result.data.login;
      await secureSetItem('accessToken', access);
      await secureSetItem('refreshToken', refresh);

      // fetch the session
      const { data, error } = await getSession();

      // handle error case
      if (error) {
        if (import.meta.env.DEV) {
          toastError(error);
        }
        await secureRemoveItem('accessToken');
        await secureRemoveItem('refreshToken');
      }

      if (data?.session) {
        const { session } = data;
        import.meta.env.DEV && console.log(`[{session}]:`, session);
        // Set language based on user preferences or default to 'fr'
        i18n.changeLanguage(session.preferences.language ?? 'fr');
        // Update the login status
        loginStatusVar({
          ...loginStatusVar(),
          isLogging: false,
          forceGetSession: true,
          isLoggedIn: true,
          isSessionExpired: false,
          shouldLogFirst: false,
        });
        // Redirect to the right page (only once)
        redirectAfterLogin();
      } else {
        import.meta.env.DEV && console.log('Get session failed at login.');
        loginStatusVar({
          ...loginStatusVar(),
          isLogging: false,
          isLoggedIn: false,
          isSessionExpired: false,
          shouldLogFirst: true,
        });

        await secureRemoveItem('accessToken');
        await secureRemoveItem('refreshToken');
      }
    } catch (error) {}
  };

  const login = async (args: Apollo.MutationHookOptions<LoginMutation, LoginMutationVariables>) => {
    try {
      loginStatusVar({ ...loginStatusVar(), isLogging: true });
      const tokens = await logUser(args);
      await handleLogin(tokens);
    } catch (error) {
      const parsedError = parseApolloError(error as Apollo.ApolloError);
      if (import.meta.env.DEV) {
        toastError(error);
      }
      loginStatusVar({ ...loginStatusVar(), isLogging: false });
      throw parsedError;
    }
  };

  const [logoutUser, { client }] = useLogoutMutation({
    async onCompleted() {
      // clear the cache (unlike resetStore, clearStore does not refetch queries)
      await client.clearStore();

      // unregister PWA push notifications
      await pushNotifUnRegister();

      // remove the access and refresh tokens from secure storage if on native platform (Capacitor)
      await secureRemoveItem('accessToken');
      await secureRemoveItem('refreshToken');

      // make sure to clear the cache for the following queries
      localStorage?.removeItem('SPOTS_LAST_CALL');
      localStorage?.removeItem('EVENTS_LAST_CALL');

      loginStatusVar({
        forceGetSession: true, // get session without cache
        isLoggedIn: false,
        isLogging: false,
        mustFinalizeRegister: false,
        isSessionExpired: false,
        shouldLogFirst: false,
        redirection: null,
      });

      setTimeout(() => {
        // wait a little bit before navigating to avoid flickering
        navigate('/spots');
      }, 300);
    },
  });

  const logout = async () => {
    const FCMToken = Capacitor.isNativePlatform()
      ? ((await FirebaseMessaging.getToken())?.token ?? null)
      : null;

    await logoutUser({ variables: { FCMToken } });

    Capacitor.isNativePlatform() && (await FirebaseMessaging.deleteToken());
  };

  return { logout, login, getSession };
}

async function pushNotifUnRegister() {
  try {
    import.meta.env.DEV && console.log('🔔 Unregistering PWA push notifications...');
  } catch (error) {
    console.error('[pushNotifUnRegister error]:', error);
  }
}
